import React from 'react';
import analyticsService from '../services/analyticsService';
import { logger } from '../utils/logger';
import frontendErrorLogger from '../api/frontendErrorLogger';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Logs errors to backend with sensitive data masking and displays a fallback UI
 * Tracks errors in analytics for observability
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error using centralized logger (automatically sanitizes and sends to backend)
    logger.error('React Error Boundary caught error', error, {
      errorId,
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'root',
    });

    // Also log to dedicated frontend error logger
    frontendErrorLogger.logError({
      name: error.name || 'ReactError',
      message: error.message || 'React component error',
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      context: {
        errorId,
        errorBoundary: this.props.name || 'root',
        type: 'react_error_boundary'
      }
    });

    // Track error in analytics
    analyticsService.trackError(error, {
      errorId,
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'root',
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div style={{
          padding: '2rem',
          margin: '2rem auto',
          maxWidth: '600px',
          textAlign: 'center',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
        }}>
          <h2 style={{ color: '#856404', marginBottom: '1rem' }}>
            ⚠️ Something went wrong
          </h2>
          <p style={{ color: '#856404', marginBottom: '1rem' }}>
            We're sorry for the inconvenience. The application encountered an unexpected error.
          </p>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ 
              textAlign: 'left', 
              backgroundColor: '#fff', 
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ 
                fontSize: '0.875rem', 
                overflow: 'auto',
                color: '#d32f2f',
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Go to Homepage
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
